/**
 * GRANULLO PLATFORM EVOLUTION — Program Integrity Context
 * 
 * Phase 1: Program Integrity Core Model
 * 
 * This context EXTENDS the existing program model with the 10 integrity dimensions.
 * The program object remains the SINGLE SOURCE OF TRUTH.
 * 
 * ARCHITECTURE CONSTRAINT: No duplicate models. Extends only.
 * 
 * Dimensions:
 * 1. Institution (KYC + ZKP verified)
 * 2. Participants (all stakeholders registered)
 * 3. Authority Matrix (roles assigned)
 * 4. System Blueprint (graph model)
 * 5. Policy Rules (constraints defined)
 * 6. Baseline Signals (telemetry configured)
 * 7. Decision Matrix (engine configured)
 * 8. Simulation Model (stress tests passed)
 * 9. Monitoring Layer (telemetry active)
 * 10. Exposure Evaluation (risk assessed)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Program Integrity Score - Calculated readiness metric
 */
export const INTEGRITY_DIMENSIONS = {
  INSTITUTION: { id: 'institution', name: 'Institution', weight: 15 },
  PARTICIPANTS: { id: 'participants', name: 'Participants', weight: 10 },
  AUTHORITY_MATRIX: { id: 'authority_matrix', name: 'Authority Matrix', weight: 10 },
  SYSTEM_BLUEPRINT: { id: 'system_blueprint', name: 'System Blueprint', weight: 15 },
  POLICY_RULES: { id: 'policy_rules', name: 'Policy Rules', weight: 10 },
  BASELINE_SIGNALS: { id: 'baseline_signals', name: 'Baseline Signals', weight: 10 },
  DECISION_MATRIX: { id: 'decision_matrix', name: 'Decision Matrix', weight: 10 },
  SIMULATION_MODEL: { id: 'simulation_model', name: 'Simulation Model', weight: 10 },
  MONITORING_LAYER: { id: 'monitoring_layer', name: 'Monitoring Layer', weight: 5 },
  EXPOSURE_EVALUATION: { id: 'exposure_evaluation', name: 'Exposure Evaluation', weight: 5 }
};

/**
 * Launch Readiness Gate - 11 gates that must pass before launch
 */
export const LAUNCH_GATES = {
  INSTITUTION_VERIFIED: { id: 'institution_verified', name: 'Institution Verified', path: '/onboarding/kyc-vault' },
  PARTICIPANTS_REGISTERED: { id: 'participants_registered', name: 'Participants Registered', path: '/program/participants' },
  AUTHORITY_ROLES_ASSIGNED: { id: 'authority_roles_assigned', name: 'Authority Roles Assigned', path: '/authority' },
  BLUEPRINT_DEFINED: { id: 'blueprint_defined', name: 'Blueprint Defined', path: '/program/blueprint' },
  POLICY_RULES_DEFINED: { id: 'policy_rules_defined', name: 'Policy Rules Defined', path: '/program/policy' },
  BASELINE_SIGNALS_DEFINED: { id: 'baseline_signals_defined', name: 'Baseline Signals Defined', path: '/program/signals' },
  DECISION_MATRIX_DEFINED: { id: 'decision_matrix_defined', name: 'Decision Matrix Defined', path: '/program/decisions' },
  SIMULATION_TESTS_PASSED: { id: 'simulation_tests_passed', name: 'Simulation Tests Passed', path: '/simulation' },
  MONITORING_LAYER_ACTIVE: { id: 'monitoring_layer_active', name: 'Monitoring Layer Active', path: '/program/monitoring' },
  DOCUMENTATION_STORED: { id: 'documentation_stored', name: 'Documentation Stored', path: '/program/docs' },
  BILLING_VALIDATED: { id: 'billing_validated', name: 'Billing Validated', path: '/billing' }
};

/**
 * Gate Status Types
 */
export const GATE_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  PASSED: 'passed',
  FAILED: 'failed',
  BLOCKED: 'blocked'
};

/**
 * Launch Status Types
 */
export const LAUNCH_STATUS = {
  INCOMPLETE: 'incomplete',
  READY: 'ready',
  LAUNCHED: 'launched',
  SUSPENDED: 'suspended',
  TERMINATED: 'terminated'
};

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * FOUNDER DIRECTIVE: Architectural Hooks for Future Systems
 * 
 * These hooks ensure Phase 3 does not hardcode structures that would block:
 * - Template inheritance
 * - Decision outcome memory
 * - Economic version pinning
 */

/**
 * ARCHITECTURAL HOOK: System Memory
 * Reserved structure for capturing structural intelligence from all programs.
 * Phase 8 implementation will populate this with decision outcomes and patterns.
 */
const createSystemMemoryHook = (programId) => ({
  programId,
  enabled: false,
  // Decision outcome memory (Phase 8)
  decisionOutcomes: [],
  // Pattern recognition data (Phase 8)
  patterns: [],
  // Structural intelligence insights (Phase 8)
  insights: [],
  // Version for forward compatibility
  hookVersion: '1.0',
  createdAt: new Date().toISOString()
});

/**
 * ARCHITECTURAL HOOK: Verified System Blueprints
 * Reserved structure for template inheritance and verification.
 * Phase 8 implementation will support verified/audited blueprint templates.
 */
const createVerifiedBlueprintHook = (programId) => ({
  programId,
  enabled: false,
  // Template reference (if derived from verified template)
  templateRef: null,
  // Verification status
  verificationStatus: 'unverified', // 'unverified' | 'pending' | 'verified' | 'certified'
  // Verification metadata
  verifiedBy: null,
  verifiedAt: null,
  // Audit trail for blueprint changes
  auditTrail: [],
  // Version for forward compatibility
  hookVersion: '1.0',
  createdAt: new Date().toISOString()
});

/**
 * ARCHITECTURAL HOOK: Commit Engine Versioning
 * Reserved structure for economic version pinning.
 * Phase 8 implementation will support commit pricing version lock.
 */
const createCommitVersioningHook = (programId) => ({
  programId,
  enabled: false,
  // Current commit economy version
  commitEngineVersion: '1.0',
  // Version pinning (locks pricing at a specific version)
  versionPinned: false,
  pinnedVersion: null,
  pinnedAt: null,
  // Pricing snapshot at pin time
  pricingSnapshot: null,
  // Version migration history
  versionHistory: [],
  // Version for forward compatibility
  hookVersion: '1.0',
  createdAt: new Date().toISOString()
});

/**
 * Create default integrity extension for a program
 * 
 * FOUNDER DIRECTIVE: Program Object Structure
 * ProgramObject {
 *   blueprint_graph (systemBlueprint)
 *   baseline_signals (baselineSignals)
 *   ... other dimensions
 *   ... architectural hooks for future systems
 * }
 */
const createDefaultIntegrity = (programId) => ({
  programId,
  
  // Dimension 4: System Blueprint (Phase 2 - IMPLEMENTED)
  // FOUNDER DIRECTIVE: blueprint_graph
  systemBlueprint: {
    id: `blueprint_${programId}`,
    nodes: [],
    edges: [],
    layers: [],
    version: '1.0'
  },
  
  // Dimension 5: Policy Rules
  policyRules: [],
  
  // Dimension 6: Baseline Signals (Phase 2 - IMPLEMENTED)
  // FOUNDER DIRECTIVE: baseline_signals
  baselineSignals: [],
  
  // Dimension 7: Decision Matrix (Phase 3 - NEXT)
  decisionMatrix: {
    entries: [],
    escalationPaths: []
  },
  
  // Dimension 8: Simulation Model
  simulationModel: {
    scenarios: [],
    stressTestResults: [],
    lastSimulationAt: null
  },
  
  // Dimension 9: Monitoring Layer (placeholder - Phase 4)
  monitoringLayer: {
    enabled: false,
    refreshRate: 'hourly',
    alertThresholds: [],
    activeAlerts: []
  },
  
  // Dimension 10: Exposure Evaluation (placeholder - Phase 4)
  exposureEvaluation: {
    overallScore: 0,
    riskFactors: [],
    cascadeAnalysis: null,
    lastEvaluatedAt: null
  },
  
  // ============================================================================
  // FOUNDER DIRECTIVE: ARCHITECTURAL HOOKS FOR FUTURE SYSTEMS
  // These placeholders ensure Phase 3+ does not hardcode structures that would block:
  // - Template inheritance
  // - Decision outcome memory
  // - Economic version pinning
  // ============================================================================
  
  // HOOK: System Memory (Phase 8)
  // Captures structural intelligence from decision outcomes
  systemMemoryHook: createSystemMemoryHook(programId),
  
  // HOOK: Verified Blueprints (Phase 8)
  // Supports template inheritance and audit verification
  verifiedBlueprintHook: createVerifiedBlueprintHook(programId),
  
  // HOOK: Commit Engine Versioning (Phase 8)
  // Supports economic version pinning for pricing stability
  commitVersioningHook: createCommitVersioningHook(programId)
});

/**
 * Create default dimension scores
 */
const createDefaultDimensionScores = () => {
  const scores = {};
  Object.values(INTEGRITY_DIMENSIONS).forEach(dim => {
    scores[dim.id] = {
      dimension: dim.name,
      score: 0,
      status: 'incomplete',
      weight: dim.weight,
      issues: [],
      recommendations: []
    };
  });
  return scores;
};

/**
 * Create default launch readiness gate
 */
const createDefaultLaunchGate = (programId) => {
  const gates = {};
  Object.values(LAUNCH_GATES).forEach(gate => {
    gates[gate.id] = {
      gateId: gate.id,
      name: gate.name,
      status: GATE_STATUS.PENDING,
      verifiedBy: null,
      verifiedAt: null,
      requirements: [],
      completedRequirements: [],
      blockingIssues: [],
      verificationPath: gate.path
    };
  });
  
  return {
    programId,
    gates,
    passedCount: 0,
    totalGates: Object.keys(LAUNCH_GATES).length,
    overallStatus: LAUNCH_STATUS.INCOMPLETE,
    approvedBy: null,
    approvedAt: null,
    launchedAt: null
  };
};

// ============================================================================
// CONTEXT
// ============================================================================

const ProgramIntegrityContext = createContext(null);

export function ProgramIntegrityProvider({ children }) {
  // State for the currently active program's integrity
  const [activeProgram, setActiveProgram] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [dimensionScores, setDimensionScores] = useState(createDefaultDimensionScores());
  const [launchGate, setLaunchGate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // ============================================================================
  // INTEGRITY SCORE CALCULATION
  // ============================================================================
  
  /**
   * Calculate overall integrity score from dimension scores
   */
  const calculateOverallScore = useCallback((scores) => {
    let totalWeightedScore = 0;
    let totalWeight = 0;
    
    Object.values(scores).forEach(dimScore => {
      totalWeightedScore += dimScore.score * dimScore.weight;
      totalWeight += dimScore.weight;
    });
    
    return totalWeight > 0 ? Math.round(totalWeightedScore / totalWeight) : 0;
  }, []);
  
  /**
   * Evaluate a single dimension's score based on its configuration
   */
  const evaluateDimensionScore = useCallback((dimensionId, programData, integrityData) => {
    const evaluators = {
      // Dimension 1: Institution verified via KYC + ZKP
      institution: () => {
        const hasKyc = programData?.kyc_verified === true;
        const hasZkp = programData?.zkp_validated === true;
        const hasOrg = !!programData?.org_id;
        
        let score = 0;
        const issues = [];
        const recommendations = [];
        
        if (hasOrg) score += 30;
        else issues.push('Organization not linked');
        
        if (hasKyc) score += 40;
        else {
          issues.push('KYC verification incomplete');
          recommendations.push('Complete KYC verification in Identity Vault');
        }
        
        if (hasZkp) score += 30;
        else recommendations.push('Enable ZKP validation for enhanced privacy');
        
        return { score, issues, recommendations };
      },
      
      // Dimension 2: Participants registered
      participants: () => {
        const participantCount = programData?.beneficiary_count || 0;
        const hasParticipants = participantCount > 0;
        
        let score = hasParticipants ? 100 : 0;
        const issues = [];
        const recommendations = [];
        
        if (!hasParticipants) {
          issues.push('No participants registered');
          recommendations.push('Register program participants');
        }
        
        return { score, issues, recommendations };
      },
      
      // Dimension 3: Authority Matrix configured
      authority_matrix: () => {
        const hasAuthority = !!programData?.authority_matrix_configured;
        const rolesAssigned = programData?.roles_assigned_count || 0;
        
        let score = 0;
        const issues = [];
        const recommendations = [];
        
        if (hasAuthority) score += 50;
        else issues.push('Authority matrix not configured');
        
        if (rolesAssigned >= 3) score += 50;
        else if (rolesAssigned > 0) {
          score += 25;
          recommendations.push('Assign more authority roles for complete governance');
        } else {
          issues.push('No authority roles assigned');
        }
        
        return { score, issues, recommendations };
      },
      
      // Dimension 4: System Blueprint defined
      system_blueprint: () => {
        const blueprint = integrityData?.systemBlueprint;
        const hasNodes = blueprint?.nodes?.length > 0;
        const hasEdges = blueprint?.edges?.length > 0;
        const hasLayers = blueprint?.layers?.length > 0;
        
        let score = 0;
        const issues = [];
        const recommendations = [];
        
        if (hasNodes) score += 40;
        else {
          issues.push('No blueprint nodes defined');
          recommendations.push('Define participant nodes in system blueprint');
        }
        
        if (hasEdges) score += 40;
        else {
          issues.push('No flow edges defined');
          recommendations.push('Define flow relationships between nodes');
        }
        
        if (hasLayers) score += 20;
        else recommendations.push('Organize nodes into logical layers');
        
        return { score, issues, recommendations };
      },
      
      // Dimension 5: Policy Rules defined
      policy_rules: () => {
        const hasPolicy = !!programData?.policy_id;
        const rules = integrityData?.policyRules || [];
        const ruleCount = rules.length;
        
        let score = 0;
        const issues = [];
        const recommendations = [];
        
        if (hasPolicy) score += 50;
        else {
          issues.push('No policy linked');
          recommendations.push('Create and link program policy');
        }
        
        if (ruleCount >= 3) score += 50;
        else if (ruleCount > 0) {
          score += 25;
          recommendations.push('Define additional policy rules for complete coverage');
        } else {
          issues.push('No custom policy rules defined');
        }
        
        return { score, issues, recommendations };
      },
      
      // Dimension 6: Baseline Signals defined
      baseline_signals: () => {
        const signals = integrityData?.baselineSignals || [];
        const signalCount = signals.length;
        
        let score = 0;
        const issues = [];
        const recommendations = [];
        
        if (signalCount >= 5) score = 100;
        else if (signalCount >= 3) {
          score = 70;
          recommendations.push('Define additional baseline signals for comprehensive monitoring');
        } else if (signalCount > 0) {
          score = 40;
          recommendations.push('Add more baseline signals (recommend 5+)');
        } else {
          issues.push('No baseline signals defined');
          recommendations.push('Define telemetry signals for drift detection');
        }
        
        return { score, issues, recommendations };
      },
      
      // Dimension 7: Decision Matrix configured
      decision_matrix: () => {
        const matrix = integrityData?.decisionMatrix;
        const entries = matrix?.entries || [];
        const escalations = matrix?.escalationPaths || [];
        
        let score = 0;
        const issues = [];
        const recommendations = [];
        
        if (entries.length >= 3) score += 60;
        else if (entries.length > 0) {
          score += 30;
          recommendations.push('Define additional decision matrix entries');
        } else {
          issues.push('No decision matrix entries defined');
          recommendations.push('Configure decision pathways for signal drift');
        }
        
        if (escalations.length > 0) score += 40;
        else recommendations.push('Define escalation paths');
        
        return { score, issues, recommendations };
      },
      
      // Dimension 8: Simulation tests passed
      simulation_model: () => {
        const model = integrityData?.simulationModel;
        const scenarios = model?.scenarios || [];
        const results = model?.stressTestResults || [];
        const passedTests = results.filter(r => r.status === 'passed').length;
        
        let score = 0;
        const issues = [];
        const recommendations = [];
        
        if (scenarios.length >= 3) score += 40;
        else if (scenarios.length > 0) {
          score += 20;
          recommendations.push('Add more simulation scenarios');
        } else {
          issues.push('No simulation scenarios configured');
        }
        
        if (results.length > 0 && passedTests === results.length) {
          score += 60;
        } else if (passedTests > 0) {
          score += 30;
          issues.push(`${results.length - passedTests} stress test(s) failed`);
        } else {
          issues.push('No simulation tests completed');
          recommendations.push('Run stress tests before launch');
        }
        
        return { score, issues, recommendations };
      },
      
      // Dimension 9: Monitoring layer active
      monitoring_layer: () => {
        const monitoring = integrityData?.monitoringLayer;
        const isEnabled = monitoring?.enabled === true;
        const alertCount = monitoring?.alertThresholds?.length || 0;
        
        let score = 0;
        const issues = [];
        const recommendations = [];
        
        if (isEnabled) score += 60;
        else {
          issues.push('Monitoring layer not enabled');
          recommendations.push('Enable real-time monitoring');
        }
        
        if (alertCount >= 3) score += 40;
        else if (alertCount > 0) {
          score += 20;
          recommendations.push('Configure additional alert thresholds');
        } else {
          recommendations.push('Configure alert thresholds for drift detection');
        }
        
        return { score, issues, recommendations };
      },
      
      // Dimension 10: Exposure evaluation complete
      exposure_evaluation: () => {
        const exposure = integrityData?.exposureEvaluation;
        const hasScore = exposure?.overallScore > 0;
        const hasRiskFactors = exposure?.riskFactors?.length > 0;
        const hasCascade = !!exposure?.cascadeAnalysis;
        
        let score = 0;
        const issues = [];
        const recommendations = [];
        
        if (hasScore) score += 40;
        else {
          issues.push('Exposure not evaluated');
          recommendations.push('Run exposure evaluation');
        }
        
        if (hasRiskFactors) score += 30;
        else recommendations.push('Identify risk factors');
        
        if (hasCascade) score += 30;
        else recommendations.push('Complete cascade analysis');
        
        return { score, issues, recommendations };
      }
    };
    
    const evaluator = evaluators[dimensionId];
    if (evaluator) {
      const result = evaluator();
      return {
        dimension: INTEGRITY_DIMENSIONS[dimensionId.toUpperCase()]?.name || dimensionId,
        score: result.score,
        status: result.score >= 80 ? 'complete' : result.score >= 40 ? 'partial' : 'incomplete',
        weight: INTEGRITY_DIMENSIONS[dimensionId.toUpperCase()]?.weight || 10,
        issues: result.issues,
        recommendations: result.recommendations
      };
    }
    
    return createDefaultDimensionScores()[dimensionId];
  }, []);
  
  /**
   * Recalculate all dimension scores
   */
  const recalculateScores = useCallback((programData, integrityData) => {
    const newScores = {};
    
    Object.values(INTEGRITY_DIMENSIONS).forEach(dim => {
      newScores[dim.id] = evaluateDimensionScore(dim.id, programData, integrityData);
    });
    
    setDimensionScores(newScores);
    return newScores;
  }, [evaluateDimensionScore]);
  
  // ============================================================================
  // LAUNCH GATE MANAGEMENT
  // ============================================================================
  
  /**
   * Update a single gate's status
   */
  const updateGateStatus = useCallback((gateId, status, verifiedBy = null) => {
    setLaunchGate(prev => {
      if (!prev) return prev;
      
      const newGates = { ...prev.gates };
      newGates[gateId] = {
        ...newGates[gateId],
        status,
        verifiedBy,
        verifiedAt: status === GATE_STATUS.PASSED ? new Date().toISOString() : null
      };
      
      // Count passed gates
      const passedCount = Object.values(newGates).filter(g => g.status === GATE_STATUS.PASSED).length;
      
      return {
        ...prev,
        gates: newGates,
        passedCount,
        overallStatus: passedCount === prev.totalGates ? LAUNCH_STATUS.READY : LAUNCH_STATUS.INCOMPLETE
      };
    });
  }, []);
  
  /**
   * Check all gates and determine launch readiness
   */
  const checkLaunchReadiness = useCallback((programData, integrityData) => {
    if (!launchGate) return false;
    
    const scores = recalculateScores(programData, integrityData);
    
    // Map dimension scores to gate statuses
    const gateMapping = {
      institution_verified: 'institution',
      participants_registered: 'participants',
      authority_roles_assigned: 'authority_matrix',
      blueprint_defined: 'system_blueprint',
      policy_rules_defined: 'policy_rules',
      baseline_signals_defined: 'baseline_signals',
      decision_matrix_defined: 'decision_matrix',
      simulation_tests_passed: 'simulation_model',
      monitoring_layer_active: 'monitoring_layer'
    };
    
    // Update gates based on dimension scores
    Object.entries(gateMapping).forEach(([gateId, dimId]) => {
      const dimScore = scores[dimId];
      if (dimScore) {
        const status = dimScore.score >= 80 ? GATE_STATUS.PASSED : 
                      dimScore.score >= 40 ? GATE_STATUS.IN_PROGRESS : 
                      GATE_STATUS.PENDING;
        updateGateStatus(gateId, status);
      }
    });
    
    // Check documentation and billing manually (not dimension-based)
    // These would be verified through separate checks
    
    return launchGate.passedCount === launchGate.totalGates;
  }, [launchGate, recalculateScores, updateGateStatus]);
  
  // ============================================================================
  // PROGRAM INTEGRITY MANAGEMENT
  // ============================================================================
  
  /**
   * Initialize integrity for a program
   */
  const initializeProgram = useCallback((program) => {
    if (!program?.program_id) return;
    
    setIsLoading(true);
    
    // Set active program
    setActiveProgram(program);
    
    // Initialize integrity extension
    const newIntegrity = createDefaultIntegrity(program.program_id);
    setIntegrity(newIntegrity);
    
    // Initialize launch gate
    const newLaunchGate = createDefaultLaunchGate(program.program_id);
    setLaunchGate(newLaunchGate);
    
    // Calculate initial scores
    recalculateScores(program, newIntegrity);
    
    setIsLoading(false);
  }, [recalculateScores]);
  
  /**
   * Update integrity data for current program
   */
  const updateIntegrity = useCallback((updates) => {
    setIntegrity(prev => {
      if (!prev) return prev;
      
      const newIntegrity = { ...prev, ...updates };
      
      // Recalculate scores after update
      if (activeProgram) {
        recalculateScores(activeProgram, newIntegrity);
      }
      
      return newIntegrity;
    });
  }, [activeProgram, recalculateScores]);
  
  /**
   * Update system blueprint
   */
  const updateBlueprint = useCallback((blueprint) => {
    updateIntegrity({ systemBlueprint: blueprint });
  }, [updateIntegrity]);
  
  /**
   * Add policy rule
   */
  const addPolicyRule = useCallback((rule) => {
    setIntegrity(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        policyRules: [...prev.policyRules, { ...rule, id: `rule_${Date.now()}` }]
      };
    });
  }, []);
  
  /**
   * Add baseline signal
   */
  const addBaselineSignal = useCallback((signal) => {
    setIntegrity(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        baselineSignals: [...prev.baselineSignals, { ...signal, id: `signal_${Date.now()}` }]
      };
    });
  }, []);
  
  /**
   * Add decision matrix entry
   */
  const addDecisionMatrixEntry = useCallback((entry) => {
    setIntegrity(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        decisionMatrix: {
          ...prev.decisionMatrix,
          entries: [...prev.decisionMatrix.entries, { ...entry, id: `dm_${Date.now()}` }]
        }
      };
    });
  }, []);
  
  // ============================================================================
  // ARCHITECTURAL HOOKS MANAGEMENT
  // FOUNDER DIRECTIVE: Future system compatibility
  // ============================================================================
  
  /**
   * Update System Memory Hook
   * For Phase 8: Decision outcome memory
   */
  const updateSystemMemoryHook = useCallback((updates) => {
    setIntegrity(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        systemMemoryHook: {
          ...prev.systemMemoryHook,
          ...updates,
          updatedAt: new Date().toISOString()
        }
      };
    });
  }, []);
  
  /**
   * Update Verified Blueprint Hook
   * For Phase 8: Template inheritance and verification
   */
  const updateVerifiedBlueprintHook = useCallback((updates) => {
    setIntegrity(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        verifiedBlueprintHook: {
          ...prev.verifiedBlueprintHook,
          ...updates,
          updatedAt: new Date().toISOString()
        }
      };
    });
  }, []);
  
  /**
   * Update Commit Versioning Hook
   * For Phase 8: Economic version pinning
   */
  const updateCommitVersioningHook = useCallback((updates) => {
    setIntegrity(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        commitVersioningHook: {
          ...prev.commitVersioningHook,
          ...updates,
          updatedAt: new Date().toISOString()
        }
      };
    });
  }, []);
  
  /**
   * Pin commit engine version
   * Locks pricing at current version for stability
   */
  const pinCommitVersion = useCallback((version) => {
    updateCommitVersioningHook({
      versionPinned: true,
      pinnedVersion: version,
      pinnedAt: new Date().toISOString()
    });
  }, [updateCommitVersioningHook]);
  
  /**
   * Add decision outcome to System Memory
   * For Phase 8: Pattern learning from decisions
   */
  const recordDecisionOutcome = useCallback((outcome) => {
    setIntegrity(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        systemMemoryHook: {
          ...prev.systemMemoryHook,
          decisionOutcomes: [
            ...prev.systemMemoryHook.decisionOutcomes,
            { ...outcome, recordedAt: new Date().toISOString() }
          ],
          updatedAt: new Date().toISOString()
        }
      };
    });
  }, []);
  
  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  /**
   * Overall integrity score
   */
  const overallScore = useMemo(() => {
    return calculateOverallScore(dimensionScores);
  }, [dimensionScores, calculateOverallScore]);
  
  /**
   * Launch readiness status
   */
  const isLaunchReady = useMemo(() => {
    return launchGate?.overallStatus === LAUNCH_STATUS.READY;
  }, [launchGate]);
  
  /**
   * Get all blocking issues
   */
  const blockingIssues = useMemo(() => {
    const issues = [];
    Object.values(dimensionScores).forEach(dim => {
      if (dim.issues.length > 0) {
        issues.push({
          dimension: dim.dimension,
          issues: dim.issues
        });
      }
    });
    return issues;
  }, [dimensionScores]);
  
  /**
   * Get all recommendations
   */
  const allRecommendations = useMemo(() => {
    const recs = [];
    Object.values(dimensionScores).forEach(dim => {
      if (dim.recommendations.length > 0) {
        recs.push({
          dimension: dim.dimension,
          recommendations: dim.recommendations
        });
      }
    });
    return recs;
  }, [dimensionScores]);
  
  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  
  const value = {
    // Constants
    INTEGRITY_DIMENSIONS,
    LAUNCH_GATES,
    GATE_STATUS,
    LAUNCH_STATUS,
    
    // State
    activeProgram,
    integrity,
    dimensionScores,
    launchGate,
    isLoading,
    
    // Computed
    overallScore,
    isLaunchReady,
    blockingIssues,
    allRecommendations,
    
    // Actions
    initializeProgram,
    updateIntegrity,
    updateBlueprint,
    addPolicyRule,
    addBaselineSignal,
    addDecisionMatrixEntry,
    updateGateStatus,
    checkLaunchReadiness,
    recalculateScores,
    
    // FOUNDER DIRECTIVE: Architectural Hooks for Future Systems
    // These functions provide compatibility for Phase 8+ features
    updateSystemMemoryHook,
    updateVerifiedBlueprintHook,
    updateCommitVersioningHook,
    pinCommitVersion,
    recordDecisionOutcome
  };
  
  return (
    <ProgramIntegrityContext.Provider value={value}>
      {children}
    </ProgramIntegrityContext.Provider>
  );
}

export function useProgramIntegrity() {
  const context = useContext(ProgramIntegrityContext);
  if (!context) {
    throw new Error('useProgramIntegrity must be used within ProgramIntegrityProvider');
  }
  return context;
}

/**
 * SAFE HOOK: Returns null if ProgramIntegrityProvider is not available
 * Use this in derived contexts that need graceful fallback for demo/standalone mode
 * This pattern avoids try-catch around hooks which violates rules-of-hooks
 */
export function useProgramIntegrityOptional() {
  const context = useContext(ProgramIntegrityContext);
  return context; // Returns null if not within provider, no throw
}

export default ProgramIntegrityContext;
